import { Popover } from '@headlessui/react'
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import React, { useState, useCallback, useMemo } from 'react'
import { usePopper } from 'react-popper'
import {
  useExportDocumentDiffMutation,
  useGetDocumentBlacklineQuery,
} from '../../redux/api-slice'
import { ProjectDocument } from '../../shared/interfaces/project/document/document.interface'
import { Tooltip } from 'react-tooltip'
import { useNavigateWorkflow } from '../../hooks/use-navigate-workflow'
import { workflows } from '../workflows/workflow-popover/workflow.data'
import { skipToken } from '@reduxjs/toolkit/query'

interface DocumentViewerPopoverProps {
  document: ProjectDocument
}

const DocumentViewerPopover: React.FC<DocumentViewerPopoverProps> = ({
  document,
}) => {
  //   const currentProject = useSelector(selectCurrentProject) as Project;
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null
  )
  const [popperElement, setPanelRef] = useState<HTMLElement | null>(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement)
  const { navigateWorkflow } = useNavigateWorkflow()
  const { currentData: documentBlacklines } = useGetDocumentBlacklineQuery(
    document?.id ?? skipToken,
    {
      pollingInterval: 3000,
    }
  )

  const [exportDocumentDiff] = useExportDocumentDiffMutation()

  const onExportDiff = useCallback(async () => {
    referenceElement?.click()
    if (!document?.id) {
      return
    }
    const response = await exportDocumentDiff(document?.id).unwrap()
    window.open(response.export_url, '_blank')
  }, [document?.id, exportDocumentDiff, referenceElement])

  const blacklineFile = useMemo(() => {
    if (!documentBlacklines?.length) {
      return null
    }
    return documentBlacklines.find(
      (blackline) => blackline.blackline_type === 2
    )?.file
  }, [documentBlacklines])

  const onClickShowDiff = useCallback(() => {
    navigateWorkflow(workflows[7])
  }, [navigateWorkflow])

  return (
    <>
      <Popover>
        <Popover.Button
          className={'px-2 py-2 hover:bg-gray-200'}
          tabIndex={-1}
          ref={setReferenceElement}
        >
          <EllipsisVerticalIcon className="h-5 w-5" />
        </Popover.Button>
        <Popover.Panel
          ref={setPanelRef}
          style={styles.popper}
          {...attributes.popper}
          className="z-50 flex flex-col rounded border bg-white shadow"
        >
          <button
            disabled={!blacklineFile}
            className={`w-48 px-4 py-2 text-left ${
              blacklineFile ? 'hover:bg-gray-100' : 'bg-gray-200'
            }`}
            onClick={onExportDiff}
          >
            <span
              data-tooltip-id={`${!blacklineFile ? 'export-diff-tooltip' : ''}`}
              data-tooltip-content="Please wait a moment, Provision is preparing the document comparison."
            >
              Export Document Comparison
            </span>
          </button>
          <button
            disabled={!blacklineFile}
            className={`w-48 px-4 py-2 text-left ${
              blacklineFile ? 'hover:bg-gray-100' : 'bg-gray-200'
            }`}
            onClick={onClickShowDiff}
          >
            <span
              data-tooltip-id={`${!blacklineFile ? 'export-diff-tooltip' : ''}`}
              data-tooltip-content="Please wait a moment, Provision is preparing the document comparison."
            >
              View Document Changes
            </span>
          </button>
        </Popover.Panel>
      </Popover>
      <Tooltip id={'export-diff-tooltip'} style={{ zIndex: 100 }} />
    </>
  )
}

export default DocumentViewerPopover
